import { type ReactNode, forwardRef } from 'react';
import { Platform, ScrollView, type ScrollViewProps, StyleSheet, View } from 'react-native';

import { useMenuClickEvents } from '@fhs/menu/src/analytics';

import { tokens } from '../../tokens';
import { Anchor } from '../anchor';

import { useAnchors } from './use-anchors';
import { useScrollIntoFirstPositionInView } from './use-scroll-into-first-position-in-view';

type HorizontalScrollerProps = Omit<
  ScrollViewProps,
  'horizontal' | 'showsHorizontalScrollIndicator' | 'alwaysBounceHorizontal' | 'bounces'
>;

const HorizontalScroller = forwardRef<ScrollView, HorizontalScrollerProps>(
  (props, forwardedRef) => {
    return (
      <ScrollView
        testID="anchor-list-horizontal"
        ref={forwardedRef}
        {...props}
        horizontal
        alwaysBounceHorizontal={false}
        bounces={false}
        showsHorizontalScrollIndicator={false}
        style={[horizontalScrollerStyles.scrollView, props.style]}
        contentContainerStyle={[
          horizontalScrollerStyles.scrollViewContent,
          props.contentContainerStyle,
        ]}
      >
        {props.children}
      </ScrollView>
    );
  }
);

const SpaceFiller = () => <View style={horizontalScrollerStyles.spaceFiller} />;

const horizontalScrollerStyles = StyleSheet.create({
  scrollView: {
    flexGrow: 0,
    backgroundColor: tokens.colors.$white,
    minWidth: '100%',
    //@ts-ignore Web only style
    ...Platform.select({
      web: {
        overscrollBehavior: 'none',
      },
      default: {},
    }),
  },
  scrollViewContent: {
    minWidth: '100%',
  },
  spaceFiller: {
    flexGrow: 9999,
    flexShrink: 0,
    borderBottomWidth: 3,
    borderColor: tokens.colors.$blackOpacity10,
  },
});

export type AnchorListHorizontalProps = HorizontalScrollerProps & {
  contentStart?: ReactNode;
  contentEnd?: ReactNode;
};
export function AnchorListHorizontal({
  contentStart = <SpaceFiller />,
  contentEnd = <SpaceFiller />,
  ...horizontalScrollerProps
}: AnchorListHorizontalProps) {
  const { handleScrollerLayout, handleScrollerScroll, scrollerRef, scrollIntoFirstPositionInView } =
    useScrollIntoFirstPositionInView('horizontal');

  const anchors = useAnchors();
  const { logMenuTabClick } = useMenuClickEvents();

  return (
    <HorizontalScroller
      ref={scrollerRef}
      onLayout={handleScrollerLayout}
      onScroll={handleScrollerScroll}
      scrollEventThrottle={200}
      {...horizontalScrollerProps}
    >
      {contentStart ? contentStart : null}
      {anchors.map((a, index) => (
        <Anchor
          key={`${a.id}-${index}`}
          isActive={a.isActive}
          onPress={() => {
            a.onPress();
            logMenuTabClick(a.title);
          }}
          title={a.title}
          scrollIntoFirstPositionInView={scrollIntoFirstPositionInView}
        />
      ))}
      {contentEnd ? contentEnd : null}
    </HorizontalScroller>
  );
}
